import { Button } from '@aecom-ai-engineering/react-components';
import { useMyUser } from '@consigli/hooks';
import { type Project } from '@consigli/types';
import { UserPlus } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ProjectFolderTile } from '@/components/project-folder-tile';
import { FeatureFlag, useFeatureFlag } from '@/feature-flags';
import { SHELL_COLUMN, SHELL_GUTTERS } from '@/layouts/app-shell/content-column';
import { Route } from '@/routes';
import { isAecomUser } from '@/util/aecom';

import { InviteUsersPanel } from './invite-users-panel';
import { ProjectOverviewTabs } from './project-overview-tabs';

/** The gap between the icon tile and the title beside it. */
const HEADER_METRICS = '[--project-header-gap:16px]';

/**
 * The tab row's own placement: flush with the column, so the first tab starts
 * where the icon tile does rather than under the title. It carried the tile
 * plus the gap as a left indent before, which lined it up with the title and
 * left the row looking inset from everything else on the page.
 *
 * The 16 above it is the nav's top padding rather than the identity band's
 * bottom one, so it belongs to the band that pins: pinned, it is the gap
 * between the breadcrumb bar and the tabs.
 */
const TAB_ROW = 'pt-4';

/**
 * AppShell's `banner` is these two bands, not one element: the identity row
 * scrolls away and the tab row stays.
 *
 * They have to be siblings for that. `position: sticky` is bounded by the
 * parent's box, so tabs nested in a 116px header would travel 116px and leave
 * with it. Returned as a fragment, both land directly in <main>, whose box is
 * the whole page — which is the travel the tabs need.
 *
 * `pt-6` is the frame's y=24. The 12 between the two is the tab row's `pt-3`,
 * on the pinned band rather than under the identity row — see TAB_ROW. The
 * frame's 116 height is still 24 + 48 + 12 + (16 + 16).
 */
const IDENTITY = 'pt-6';

/**
 * The pinned tab row.
 *
 * `top` is the breadcrumb bar's own `min-height` token, so the tabs come to
 * rest against it rather than under it. Reading the same token the bar is built
 * from is the closest the two can be tied together without measuring at
 * runtime.
 *
 * z-50 sits under the bar's z-60 and over the page. It needs its own fill:
 * pinned, it has the body scrolling beneath it, and <main>'s background is
 * behind that rather than behind this.
 *
 * The rule is here rather than on the identity band above, and spans the full
 * content region to meet the TopNavBar's rule, as the frame draws it.
 */
const TAB_BAND = [
  'sticky top-[var(--ds-space-11)] z-50',
  'bg-[var(--ds-color-bg-primary)]',
  'border-b border-[var(--ds-color-border-primary)]',
].join(' ');

/**
 * Not `bg-secondary`, and `motion-safe` — the same two calls
 * project-list-loading.tsx makes, for the same reasons.
 */
const FILL = 'bg-[var(--ds-color-bg-tertiary)]';
const PULSE = 'motion-safe:animate-pulse';

interface ProjectOverviewHeaderProps {
  /** From the route param, so the tab bar works before the project arrives. */
  projectId: string;
  /** Absent while the project is still being fetched. */
  project?: Project;
}

/**
 * The project workspace's header: identity, the invite action, and the tab bar.
 *
 * Renders before the project arrives, because the frames show the wait *under*
 * the chrome rather than in place of it — the tabs and the invite action are
 * usable while the name is still loading, and the page does not reflow when it
 * lands. Only the title and subtitle need the fetch, so only they are skeletons.
 *
 * Not named PageHeader — layouts/page-header/page-header.tsx is the legacy app
 * chrome and still renders on every route this one does not.
 */
export const ProjectOverviewHeader: FC<ProjectOverviewHeaderProps> = ({ projectId, project }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useMyUser();
  const redesigned = useFeatureFlag(FeatureFlag.AppRedesign);
  const [isInviting, setIsInviting] = useState(false);

  // "Project type · Project location". Type is constructionTypes, which the
  // creation form already labels that way; location is the address, there
  // being no separate field. Filtered rather than branched, so a missing half
  // leaves the other alone and neither leaves no line.
  const projectType = project?.constructionTypes
    ?.map((constructionType) => t(`project-construction-type.${constructionType}`))
    .join(', ');
  const subtitle = [projectType, project?.address].filter(Boolean).join(' · ');

  return (
    <>
      <header className={`${HEADER_METRICS} ${IDENTITY}`}>
        <div className={SHELL_GUTTERS}>
          <div className={SHELL_COLUMN}>
            <div className="flex items-start gap-[var(--project-header-gap)]">
              <ProjectFolderTile />

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {/* The design's `heading 4` — 20/24 — but weight 500, which the token
              says is 400. Size and line-height come from the token's values and
              the weight is set explicitly; the drift is raised upstream. */}
                {/* A bar inside the same 24px line box the title uses, so the swap
              does not move the row. Never a blank title, per the decisions log —
              a placeholder says "loading", an empty h1 says "broken". */}
                {project === undefined ? (
                  <div className="flex h-6 items-center" data-testid="project-title-placeholder">
                    <div className={`h-4 w-64 max-w-full rounded-full ${FILL} ${PULSE}`} />
                  </div>
                ) : (
                  <h1 className="truncate text-xl font-medium leading-6 text-[var(--ds-color-text-primary)]">
                    {project.name}
                  </h1>
                )}

                {/* With neither, no line at all rather than an empty one. While
              loading we do not yet know, and reserving it keeps the row
              still. */}
                {project === undefined ? (
                  <div className="flex h-5 items-center">
                    <div className={`h-3 w-40 max-w-full rounded-full ${FILL} ${PULSE}`} />
                  </div>
                ) : (
                  subtitle && (
                    <p className="truncate text-sm leading-5 text-[color:var(--ds-color-raw-stone-600)]">
                      {subtitle}
                    </p>
                  )
                )}
              </div>

              {/* Hidden for non-AECOM users, matching the invite entry point on the
            user management page — the destination is the org-wide invite form
            and carries no guard of its own, so the entry points are the gate.
            The server still decides whether an invite succeeds; this decides
            whether the door is visible.

            The design system's solid/md button already *is* the frame's button —
            #111827, radius 8, min-height 36, 8/16 padding, 14/20/500 — so this
            overrides none of it.

            Where it goes is the flag's call, and the branch is written out
            rather than left to the route. Flag-on it opens the project-scoped
            panel this frame belongs to (FA-116); flag-off it navigates to the
            org-level form, which is what it did before and the only invite
            surface the legacy app has.

            The off arm is unreachable through the router today — ProjectOverviewRoute
            redirects these URLs to the service list while the flag is off, so
            this header never renders there. It is still written: the gate is
            what the ticket asks for, and stating it here means the day the
            header is mounted from somewhere else, the legacy path is the one
            a flag-off user gets rather than the redesign leaking out. */}
              {isAecomUser(user.email) && (
                <Button
                  className="shrink-0"
                  onClick={() =>
                    redesigned
                      ? setIsInviting(true)
                      : navigate(`/${Route.USER_MANAGEMENT}/${Route.INVITE_USER}`)
                  }
                  size="md"
                  variant="solid"
                >
                  {/* The frame's `Icon / user-plus`. Stroke 1.5, read off the
                      export the same way the folder mark above was: its outer
                      edge sits at 0.833 of 16, which is Lucide's
                      `2 - strokeWidth/2` in a 24 viewBox. No size and no gap
                      here — Button.css sizes `> svg` to 16 at md and already
                      sets the frame's 8 between them.

                      Decorative: the button says "Invite Users" beside it. */}
                  <UserPlus aria-hidden="true" strokeWidth={1.5} />
                  {t('project-overview.invite-users')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className={TAB_BAND}>
        <div className={SHELL_GUTTERS}>
          <div className={SHELL_COLUMN}>
            <ProjectOverviewTabs className={TAB_ROW} projectId={projectId} />
          </div>
        </div>
      </div>

      {/* Rendered whether or not it is open, rather than mounted on demand: the
          panel slides out on close and a conditional mount would take it away
          mid-animation. It lazy-mounts its own content, so a closed panel
          costs nothing and a reopened one still starts empty — see
          invite-users-panel.tsx. */}
      <InviteUsersPanel
        onClose={() => setIsInviting(false)}
        open={isInviting}
        projectName={project?.name}
      />
    </>
  );
};
